export class MobileFilterDrawer {
  constructor(options = {}) {
    this.drawer = document.getElementById("mobile-filter-drawer");
    this.backdrop = this.drawer?.querySelector(".mobile-filter-backdrop");
    this.drawerPanel = this.drawer?.querySelector(".mobile-filter-drawer");
    this.openButton = document.querySelector(".mobile-filter-open");
    this.closeButton = this.drawer?.querySelector(".mobile-filter-close");
    this.clearButton = this.drawer?.querySelector(".mobile-filter-clear");
    this.applyButton = this.drawer?.querySelector(".mobile-filter-apply");
    this.applyButtonText = this.drawer?.querySelector(
      ".mobile-filter-apply-text"
    );
    this.applyButtonLoading = this.drawer?.querySelector(
      ".mobile-filter-loading"
    );
    this.filterCount = document.querySelector(".mobile-filter-count");
    this.container = this.drawer?.querySelector(".mobile-filter-content");

    this.productFilterInstance = options.productFilterInstance || null;
    this.isOpen = false;
    this.isLoading = false;
    this.pendingFilterDetail = null;

    this.init();
  }

  init() {
    if (!this.drawer) return;

    // Bind events
    this.openButton?.addEventListener("click", () => this.open());
    this.closeButton?.addEventListener("click", () => this.close());
    this.backdrop?.addEventListener("click", () => this.close());
    this.clearButton?.addEventListener("click", () => this.clearAllFilters());
    this.applyButton?.addEventListener("click", () => this.close());

    // Listen for filter changes to update count and button text
    window.addEventListener("productFilterChanged", (e) => {
      // Only process events from our product filter instance
      if (
        this.productFilterInstance &&
        e.detail.instanceId === this.productFilterInstance.instanceId
      ) {
        this.updateFilterCount(e.detail);
        this.updateApplyButtonText(e.detail);
      }
    });

    // Listen for pending filter state to show loading
    window.addEventListener("productFilterPending", (e) => {
      if (e.detail.instanceId === this.productFilterInstance?.instanceId) {
        if (e.detail.pending) {
          this.showLoading();
        } else {
          this.hideLoading();
        }
      }
    });

    // Also listen for immediate filter clicks to update count faster
    this.container?.addEventListener("click", (e) => {
      const filterItem = e.target.closest("[data-filter]");
      if (filterItem) {
        // Update count immediately after UI change
        setTimeout(() => this.updateFilterCount(), 10);
      }
    });

    // Handle escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) {
        this.close();
      }
    });

    // Initialize filter count
    this.updateFilterCount();
  }

  open() {
    if (!this.drawer) return;

    this.isOpen = true;
    this.drawer.classList.remove("hidden");

    // Prevent body scroll
    document.body.classList.add("mobile-filter-open");

    // Animate in with slight delay for smooth effect
    requestAnimationFrame(() => {
      if (this.backdrop) {
        this.backdrop.classList.add("visible");
      }
      if (this.drawerPanel) {
        this.drawerPanel.classList.add("opening");
      }
    });
  }

  close() {
    if (!this.drawer) return;

    this.isOpen = false;

    // Animate out
    if (this.backdrop) {
      this.backdrop.classList.remove("visible");
    }
    if (this.drawerPanel) {
      this.drawerPanel.classList.remove("opening");
    }

    // Hide after animation
    setTimeout(() => {
      this.drawer.classList.add("hidden");
      document.body.classList.remove("mobile-filter-open");
    }, 300);
  }

  clearAllFilters() {
    if (this.productFilterInstance) {
      // Cancel any pending debounced calls before clearing
      if (
        typeof this.productFilterInstance.cancelDebouncedCall === "function"
      ) {
        this.productFilterInstance.cancelDebouncedCall();
      }
      this.productFilterInstance.clearFilters();
    }

    // Small delay to let user see the checkboxes being unchecked before closing
    setTimeout(() => {
      this.close();
    }, 200);
  }

  updateFilterCount(filterDetail = null) {
    if (!this.filterCount) return;

    let count = 0;

    if (filterDetail && filterDetail.filters) {
      // Count active filters from event detail
      count = Object.keys(filterDetail.filters).filter((key) => {
        // Skip these filters from count:
        // 1. Category filter if on category page or syncUrlWithCategoryId is false
        // 2. Sorter filter - it's UI control, not data filter
        if (key === "c" && this.productFilterInstance) {
          const categoryId = this.productFilterInstance.categoryId || 0;
          const syncUrlWithCategoryId =
            this.productFilterInstance.syncUrlWithCategoryId;
          if (categoryId > 0 || !syncUrlWithCategoryId) {
            return false;
          }
        }
        if (key === "s") {
          return false; // Skip sorter
        }

        const value = filterDetail.filters[key];
        return value && value.toString().trim() !== "";
      }).length;
    } else if (this.productFilterInstance) {
      // Count from product filter instance
      const filters = this.productFilterInstance.getFilters();
      const categoryId = this.productFilterInstance.categoryId || 0;
      const syncUrlWithCategoryId =
        this.productFilterInstance.syncUrlWithCategoryId;

      count = Object.keys(filters).filter((key) => {
        // Skip these filters from count:
        // 1. Category filter if on category page or syncUrlWithCategoryId is false
        // 2. Sorter filter - it's UI control, not data filter
        if (key === "c" && (categoryId > 0 || !syncUrlWithCategoryId)) {
          return false;
        }
        if (key === "s") {
          return false; // Skip sorter
        }

        const value = filters[key];
        return value && value.toString().trim() !== "";
      }).length;
    }

    if (count > 0) {
      this.filterCount.textContent = ` (${count})`;
      this.filterCount.classList.remove("hidden");
    } else {
      this.filterCount.classList.add("hidden");
    }
  }

  // Show loading state on apply button
  showLoading() {
    if (!this.applyButtonText || !this.applyButtonLoading) return;

    this.isLoading = true;
    this.applyButtonText.classList.add("hidden");
    this.applyButtonLoading.classList.remove("hidden");
    this.applyButton.disabled = true;
  }

  // Hide loading state on apply button
  hideLoading() {
    if (!this.applyButtonText || !this.applyButtonLoading) return;

    this.isLoading = false;
    this.applyButtonText.classList.remove("hidden");
    this.applyButtonLoading.classList.add("hidden");
    this.applyButton.disabled = false;

    // Update button text with pending data if available
    if (this.pendingFilterDetail) {
      this.updateApplyButtonText(this.pendingFilterDetail);
      this.pendingFilterDetail = null;
    }
  }

  // Update apply button text with product count
  updateApplyButtonText(filterDetail = null) {
    if (!this.applyButtonText) return;

    // If loading, store the filterDetail to update later
    if (this.isLoading) {
      this.pendingFilterDetail = filterDetail;
      return;
    }

    let total = 0;

    if (filterDetail && typeof filterDetail.total !== "undefined") {
      total = filterDetail.total;
    } else if (this.productFilterInstance) {
      total = this.productFilterInstance.total || 0;
    }

    // Format number with thousand separators
    const formattedTotal = total.toLocaleString("vi-VN");
    this.applyButtonText.textContent = `Xem ${formattedTotal} sản phẩm`;
  }

  // Public method to set product filter instance
  setProductFilterInstance(instance) {
    this.productFilterInstance = instance;
    this.updateFilterCount();
    this.updateApplyButtonText();
  }
}
